import React, { useRef, useState } from "react";
import Card from "../../../../components/card";
import { Button } from "@chakra-ui/react";
import "./driverform.css";
import { Formik, useFormikContext } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import {
  createDriverApi,
  getDriverByIdApi,
  updateDriver,
} from "../../../../services/customAPI";
import Loader from "components/loader/loader";
import Navbar from "../../../../components/navbar";
import pdf from "../../../../assets/svg/pdf.svg";
import { toast } from "react-toastify";
import { t } from "i18next";
import { FinalDocArray, SelectOption, formvalues } from "types/types";
import { Types, DTO } from "shared-lib";

const Logger = (props: { setIsDocuments: (val: boolean) => void; display?: string }): JSX.Element => {
  const { setIsDocuments } = props;
  const firstRender = useRef(true);
  const formik = useFormikContext<DTO.DriverDTO>();
  const params = useParams();
  React.useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
    } else {
      if (formik.values?.documentsKey?.length > 0) {
        setIsDocuments(true);
      } else {
        if (params.id) {
          setIsDocuments(false);
        }
      }
    }
  }, [formik.values?.documentsKey, params.id, setIsDocuments]);
  return null;
};

const DriverForm = () => {
  const [paramData, setParamData] = useState<DTO.DriverDTO | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState<formvalues>({
    firstName: "",
    lastName: "",
    mobileNumber: "",
    email: "",
    address: "",
    vehicleNumber: "",
    vehicleType: "",
    vehicleName: "",
    vehicleMake: "",
    vehicleModel: "",
    profileImageKey: "",
    documentsKey: [],
    addressDocumentKey: [],
    documentsCriminalsKey: [],
  });

  const [options, setOptions] = useState<SelectOption[]>([]);
  const navigate = useNavigate();
  const params = useParams();
  const [imagePreview, setImagePreview] = useState(null);
  const [docPreview, setDocPreview] = useState(null);
  const [approved, setApproved] = useState<string>("");
  const [criminalDocPreview, setCriminalDocPreview] = useState(null);
  const [finalDoc, setFinalDoc] = useState<FinalDocArray>();
  const [finalCriminalDoc, setFinalCriminalDoc] = useState<FinalDocArray>();
  const [isdocuments, setIsDocuments] = useState(params.id ? false : false);
  const [isProfileImage] = useState(params.id ? false : false);

  const FILE_SIZE = 1 * 1024 * 1024;
  const SUPPORTED_FORMATS = [
    "image/jpg",
    "image/jpeg",
    "image/png",
    "image/webp",
  ];
  const FILE_SIZE_DOC = 5 * 1024 * 1024;
  const SUPPORTED_FORMATS_DOC = [
    "application/pdf",
    "image/jpg",
    "image/jpeg",
    "image/png",
    "image/webp",
  ];

  const digitOnlyRegex = /^[0-9]+$/;

  const fileSchema = Yup.mixed()
    .required(t("driverForm.validation.fileRequired") as string)
    .test(
      "fileSize",
      t("driverForm.validation.fileTooLarge") as string,
      (file: any) => file && file.size <= FILE_SIZE
    )
    .test(
      "fileFormat",
      t("driverForm.validation.unsupportedFormat") as string,
      (file: any) => file && SUPPORTED_FORMATS.includes(file.type)
    );

  const filesSchema = Yup.mixed()
    .required(t("driverForm.validation.fileRequired") as string)
    .test(
      "allFormats",
      t("driverForm.validation.unsupportedFormat") as string,
      (value) => {
        if (!Array.isArray(value)) return false;
        return value.every((file) =>
          SUPPORTED_FORMATS_DOC.includes(file?.type)
        );
      }
    )
    .test(
      "fileSize",
      t("driverForm.validation.fileTooLarge") as string,
      (value) => {
        if (!Array.isArray(value)) return false;
        return value.every((file) => file && file.size <= FILE_SIZE_DOC);
      }
    )
    .test(
      "allFormats",
      t("driverForm.validation.unsupportedFormat") as string,
      (value) => {
        if (!Array.isArray(value)) return false;
        return value.every((file) =>
          SUPPORTED_FORMATS_DOC.includes(file?.type)
        );
      }
    );

  const driverSchema = Yup.object({
    firstName: Yup.string()
      .trim()
      .required(t("driverForm.validation.firstNameRequired") as string)
      .min(2, t("driverForm.validation.firstNameTooShort", { min: 2 }))
      .matches(
        /^[A-Za-zÀ-ÖØ-öø-ÿ]+([ '-][A-Za-zÀ-ÖØ-öø-ÿ]+)*$/,
        t("driverForm.validation.invalidFirstName") as string
      ),

    lastName: Yup.string()
      .trim()
      .required(t("driverForm.validation.lastNameRequired") as string)
      .min(2, t("driverForm.validation.lastNameTooShort", { min: 2 }))
      .matches(
        /^[A-Za-zÀ-ÖØ-öø-ÿ]+([ '-][A-Za-zÀ-ÖØ-öø-ÿ]+)*$/,
        t("driverForm.validation.invalidLastName") as string
      ),

    mobileNumber: Yup.string()
      .trim()
      .required(t("driverForm.validation.mobileRequired") as string)
      .matches(
        digitOnlyRegex,
        t("driverForm.validation.invalidMobile") as string
      )
      .length(10, t("driverForm.validation.mobileDigits") as string),

    email: Yup.string()
      .trim()
      .lowercase()
      .required(t("driverForm.validation.emailRequired") as string)
      .email(t("driverForm.validation.emailInvalid") as string)
      .max(255, t("driverForm.validation.emailTooLong", { max: 255 })),

    address: Yup.string()
      .trim()
      .required(t("driverForm.validation.addressRequired") as string)
      .min(5, t("driverForm.validation.addressTooShort", { min: 5 }))
      .max(100, t("driverForm.validation.addressTooLong", { max: 100 })),

    image: isProfileImage ? fileSchema : Yup.mixed().notRequired(),

    documentsKey: isdocuments ? filesSchema : Yup.mixed().notRequired(),
  });

  function modifyVehicleNumber(vehicleNumber: string) {
    return `${vehicleNumber?.substring(0, 2) || ""} ${vehicleNumber?.substring(2, 4) || ""
      } ${vehicleNumber?.substring(4, 6) || ""} ${vehicleNumber?.substring(6, 10) || ""
      }`;
  }

  const handleCreateDriver = async (values: formvalues) => {
    setIsLoading(true);
    const formData = new FormData();

    formData.append("firstName", `${values.firstName} ${values.lastName}`);
    formData.append("mobileNumber", values.mobileNumber);
    formData.append("email", values.email);
    formData.append("address", values.address);
    formData.append("vehicleNumber", values.vehicleNumber);
    formData.append("vehicleName", values.vehicleName);
    formData.append("vehicleType", values.vehicleType);

    if (values.image && values.image.length > 0) {
      formData.append("profileImage", values.image[0]);
    }

    if (values.documents && values.documents.length > 0) {
      for (let i = 0; i < values.documents.length; i++) {
        formData.append("documents", values.documents[i]);
      }
    }

    try {
      const response = await createDriverApi(formData);

      if (!response) {
        throw new Error("Erro ao enviar os dados");
      }

      toast.success("Motorista cadastrado com sucesso!");
      setIsLoading(false);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error(errorMessage);
      setIsLoading(false);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      const driverApproved = await updateDriver(Number(id), {
        approved: Types.ApprovalStatus.APPROVED,
      });
      if (driverApproved) {
        const res = await getDriverByIdApi(Number(id));
        setApproved(res.data.approved || "");
        toast.success(t("Driver approved successfully"));
      }
      return driverApproved;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("error", errorMessage);
      toast.error(t(`${errorMessage}`));
      throw new Error(errorMessage);
    }
  };

  const handleClickForRejectDoc = async (id: any) => {
    try {
      const driverRejected = await updateDriver(Number(id), {
        approved: Types.ApprovalStatus.REJECTED,
      });
      if (driverRejected) {
        const res = await getDriverByIdApi(Number(id));
        setApproved(res.data.approved || "");
        toast.success(t("Driver rejected successfully"));
      }
      return driverRejected;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("error", errorMessage);
      toast.error(`${errorMessage}`);
      throw new Error(errorMessage);
    }
  };
  const getData = async (id: string) => {
    setIsLoading(true);
    try {
      const res = await getDriverByIdApi(Number(id));
      setParamData(res.data);
      setInitialFormValues({
        firstName: `${res.data.firstName} ${res.data.lastName || ""}`,
        lastName: res.data.lastName || "",
        mobileNumber: res.data.mobileNumber,
        email: res.data.email || "",
        address: res.data.address || "",
        vehicleNumber: res.data.vehicle?.vehicleNumber,
        vehicleType: res.data.vehicle?.vehicleCategory?.name ?? "",
        vehicleName: res.data.vehicle?.vehicleModel || "",
        vehicleMake: res.data.vehicle?.vehicleMake,
        vehicleModel: res.data.vehicle?.vehicleModel,
        profileImageKey: res.data.profileImageKey,
        documentsKey: res.data.documentsKey,
        addressDocumentKey: res.data.addressDocumentKey,
        documentsCriminalsKey: res.data.documentsCriminalsKey || [],
      });
      setApproved(res.data.approved || "");
      const profileImagePath = res.data?.profileImageKey;
      if (profileImagePath) {
        const imageUrl = `${process.env.REACT_APP_BASE_URL}/uploads/${profileImagePath}`;
        setImagePreview(imageUrl);
      }

      const docsPath = res?.data?.documentsKey?.[0];
      if (docsPath) {
        const fileUrl = `${process.env.REACT_APP_BASE_URL}/uploads/${docsPath}`;
        setFinalDoc({ key: docsPath, url: fileUrl });
        setDocPreview(fileUrl);
      }
      const addressDocumentKeyPath = res?.data?.addressDocumentKey?.[0];
      if (addressDocumentKeyPath) {
        const fileUrl = `${process.env.REACT_APP_BASE_URL}/uploads/${addressDocumentKeyPath}`;
        setFinalDoc({ key: addressDocumentKeyPath, url: fileUrl });
        setDocPreview(fileUrl);
      }

      const criminalDocsPath = res?.data?.documentsCriminalsKey?.[0];
      if (criminalDocsPath) {
        const criminalFileUrl = `${process.env.REACT_APP_BASE_URL}/uploads/${criminalDocsPath}`;
        setFinalCriminalDoc({ key: criminalDocsPath, url: criminalFileUrl });
        setCriminalDocPreview(criminalFileUrl);
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("Erro ao buscar dados do motorista:", errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDivClickDoc = async () => {
    if (docPreview) {
      window.open(docPreview, "_blank");
    }
  };

  const handleDivClickCriminalDoc = async () => {
    if (criminalDocPreview) {
      window.open(criminalDocPreview, "_blank");
    }
  };

  const handleDivClickImg = () => {
    if (imagePreview) {
      window.open(imagePreview, "_blank");
    }
  };

  React.useEffect(() => {
    const vehicleNumber = paramData?.vehicle?.vehicleNumber;
    if (options.length > 0 && vehicleNumber) {
      if (options[options.length - 1]?.value !== vehicleNumber) {
        setOptions([
          ...options,
          {
            value: vehicleNumber,
            label: modifyVehicleNumber(vehicleNumber),
          },
        ]);
      }
    }
  }, [options, paramData]);
  React.useEffect(() => {
    if (params.id) {
      getData(params.id);
    }
  }, [params]);

  return (
    <>
      <Navbar flag={false} brandText={t("driverForm.Driver")} />
      {isLoading ? (
        <Loader />
      ) : (
        <Card extra={"w-full pb-6 p-4 mt-4 pt-10"}>
          <header className="relative flex items-center justify-between ps-20">
            {params.id ? (
              <div className="text-xl font-bold text-navy-700 dark:text-white">
                {t("driverForm.Driver")}
              </div>
            ) : (
              <div className="text-xl font-bold text-navy-700 dark:text-white">
                {t("driverForm.addDriver")}
              </div>
            )}
          </header>
          <div className="p-10 pb-5 pe-20 ps-20">
            <Formik
              enableReinitialize={true}
              initialValues={initialFormValues}
              onSubmit={(values) => handleCreateDriver(values)}
              validationSchema={driverSchema}
            >
              {({
                handleChange,
                handleBlur,
                handleSubmit,
                values,
                errors,
                touched,
              }) => (
                <form onSubmit={handleSubmit}>
                  <Logger display="hidden" setIsDocuments={setIsDocuments} />

                  <div className="flex justify-between">
                    <div className="mb-3 me-6 w-full">
                      <label
                        htmlFor="firstName"
                        className="input-custom-label dark:text-white"
                      >
                        {t("Name")}
                      </label>
                      <input
                        required
                        className="mt-2 h-12 w-full rounded-xl border bg-white/0 p-3 text-sm outline-none"
                        name="firstName"
                        type="text"
                        id="firstName"
                        placeholder={t("driverForm.form.namePlaceholder")}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values?.firstName}
                        disabled
                      />
                      <div className="error-input">
                        {errors.firstName && touched.firstName
                          ? errors.firstName
                          : null}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <div className="mb-3 me-6 w-full">
                      <label
                        htmlFor="email"
                        className="input-custom-label dark:text-white"
                      >
                        {t("Email")}
                      </label>
                      <input
                        required
                        className="mt-2 h-12 w-full rounded-xl border bg-white/0 p-3 text-sm outline-none"
                        name="email"
                        type="email"
                        disabled
                        id="email"
                        placeholder={t("Enter email here")}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values?.email}
                      />
                      <div className="error-input">
                        {errors.email && touched.email ? errors.email : null}
                      </div>
                    </div>
                    <div className="mb-3 ms-6 w-full">
                      <label
                        htmlFor="address"
                        className="input-custom-label dark:text-white"
                      >
                        {t("Address")}
                      </label>
                      <input
                        required
                        className="mt-2 h-12 w-full rounded-xl border bg-white/0 p-3 text-sm outline-none"
                        name="address"
                        type="text"
                        id="address"
                        placeholder={t("Enter address here")}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled
                        value={values?.address}
                      />
                      <div className="error-input">
                        {errors.address && touched.address
                          ? errors.address
                          : null}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <div className="mb-3 me-6 w-full">
                      <label
                        htmlFor="mobileNumber"
                        className="input-custom-label dark:text-white"
                      >
                        {t("Mobile Number")}
                      </label>
                      <input
                        required
                        style={{
                          backgroundColor: "rgba(242, 242, 242, 0.5)",
                        }}
                        className="mt-2 h-12 w-full rounded-xl border bg-white/0 p-3 text-sm outline-none"
                        name="mobileNumber"
                        type="text"
                        id="mobileNumber"
                        placeholder={t("Enter vehicle type here")}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values?.mobileNumber}
                        disabled
                      />
                      <div className="error-input">
                        {errors.mobileNumber && touched.mobileNumber
                          ? errors.mobileNumber
                          : null}
                      </div>
                    </div>
                    <div className="mb-3 ms-6 w-full">
                      <label
                        htmlFor="vehicleNumber"
                        className="input-custom-label dark:text-white"
                      >
                        {t("driverForm.form.vehicleNumber")}
                      </label>
                      <input
                        required
                        style={{
                          backgroundColor: "rgba(242, 242, 242, 0.5)",
                        }}
                        className="mt-2 h-12 w-full rounded-xl border bg-white/0 p-3 text-sm outline-none"
                        name="vehicleNumber"
                        type="text"
                        id="vehicleNumber"
                        placeholder={t("Enter vehicle type here")}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values?.vehicleNumber}
                        disabled
                      />
                      <div className="error-input">
                        {errors.vehicleNumber && touched.vehicleNumber
                          ? errors.vehicleNumber
                          : null}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <div className="mb-3 me-6 w-full">
                      <label
                        htmlFor="vehicleName"
                        className="input-custom-label dark:text-white"
                      >
                        {t("Vehicle Nickname")}
                      </label>
                      <input
                        required
                        style={{
                          backgroundColor: "rgba(242, 242, 242, 0.5)",
                        }}
                        className="mt-2 h-12 w-full rounded-xl border bg-white/0 p-3 text-sm outline-none"
                        name="vehicleName"
                        type="text"
                        id="vehicleName"
                        placeholder={t("Enter vehicle nickname here")}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values?.vehicleName}
                        disabled
                      />
                      <div className="error-input">
                        {errors.vehicleName && touched.vehicleName
                          ? errors.vehicleName
                          : null}
                      </div>
                    </div>
                    <div className="mb-3 ms-6 w-full">
                      <label
                        htmlFor="vehicleType"
                        className="input-custom-label dark:text-white"
                      >
                        {t("Vehicle Type")}
                      </label>
                      <input
                        required
                        style={{
                          backgroundColor: "rgba(242, 242, 242, 0.5)",
                        }}
                        className="mt-2 h-12 w-full rounded-xl border bg-white/0 p-3 text-sm outline-none"
                        name="vehicleType"
                        type="text"
                        id="vehicleType"
                        placeholder={t("Enter vehicle type here")}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values?.vehicleType}
                        disabled
                      />
                      <div className="error-input">
                        {errors.vehicleType &&
                          touched.vehicleType &&
                          typeof errors.vehicleType === "string"
                          ? errors.vehicleType
                          : null}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <div className="mb-3 me-6 w-full">
                      <label className="input-custom-label dark:text-white">
                        {t("imageProfile")}
                      </label>
                      <div className="mt-2">
                        {imagePreview && (
                          <>
                            <div
                              className="image-preview"
                              style={{
                                width: "65px",
                                height: "65px",
                                padding: "2px",
                                border: "2px solid #9CA3AF",
                                borderRadius: "4px",
                              }}
                            >
                              <img
                                src={imagePreview}
                                style={{
                                  objectFit: "contain",
                                  height: "100%",
                                  width: "auto",
                                  cursor: "pointer",
                                  padding: "5px",
                                }}
                                alt="img"
                                onClick={handleDivClickImg}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="mb-3 ms-6 w-full">
                      <label className="input-custom-label dark:text-white">
                        {t("cnh")}
                      </label>
                      <div className="flex flex-row items-start">
                        {finalDoc && (
                          <div
                            className="document-container m-0 mb-2 me-2"
                            key={finalDoc.key}
                          >
                            <div
                              style={{
                                position: "relative",
                                width: "65px",
                                height: "80px",
                                padding: "2px",
                                cursor: "pointer",
                                border: "2px solid #9CA3AF",
                                borderRadius: "4px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              onClick={() => {
                                if (finalDoc?.url) {
                                  window.open(finalDoc.url, "_blank");
                                }
                              }}
                            >
                              <img
                                src={pdf}
                                style={{
                                  height: "40px",
                                  width: "auto",
                                  cursor: "pointer",
                                  marginBottom: "4px",
                                }}
                                alt="PDF Preview"
                              />
                              <p
                                style={{
                                  fontSize: "10px",
                                  textAlign: "center",
                                  margin: 0,
                                  padding: 0,
                                  wordBreak: "break-word",
                                }}
                              >
                                {finalDoc.key.slice(0, 6)}
                              </p>
                            </div>
                            <a
                              href={finalDoc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                fontSize: "10px",
                                color: "#7551FF",
                                marginTop: "4px",
                                textDecoration: "underline",
                                cursor: "pointer",
                                display: "block",
                                textAlign: "center",
                              }}
                            >
                              {t("Download")}
                            </a>
                          </div>
                        )}
                      </div>

                      {/* Status adicionado abaixo do PDF da CNH */}
                      <div className="mt-4">
                        <label className="input-custom-label dark:text-white">
                          {t("Status")}
                        </label>
                        <div>
                          {approved === Types.ApprovalStatus.PENDING && (
                            <span className="font-bold text-yellow-500">
                              {t("Pending")}
                            </span>
                          )}
                          {approved === Types.ApprovalStatus.REJECTED && (
                            <span className="font-bold text-red-500">
                              {t("Rejected")}
                            </span>
                          )}
                          {approved === Types.ApprovalStatus.APPROVED && (
                            <span className="font-bold text-green-500">
                              {t("Approved")}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Criminal Documents Section */}
                  <div className="flex justify-between">
                    <div className="mb-3 me-6 w-full">
                      <label className="input-custom-label dark:text-white">
                        {t("CriminalRecordDocument")}
                      </label>
                      <div className="flex flex-row items-start">
                        {finalCriminalDoc && (
                          <div
                            className="document-container m-0 mb-2 me-2"
                            key={finalCriminalDoc.key}
                          >
                            <div
                              style={{
                                position: "relative",
                                width: "65px",
                                height: "80px",
                                padding: "2px",
                                cursor: "pointer",
                                border: "2px solid #9CA3AF",
                                borderRadius: "4px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              onClick={() => {
                                if (finalCriminalDoc?.url) {
                                  window.open(finalCriminalDoc.url, "_blank");
                                }
                              }}
                            >
                              <img
                                src={pdf}
                                style={{
                                  height: "40px",
                                  width: "auto",
                                  cursor: "pointer",
                                  marginBottom: "4px",
                                }}
                                alt="PDF Preview"
                              />
                              <p
                                style={{
                                  fontSize: "10px",
                                  textAlign: "center",
                                  margin: 0,
                                  padding: 0,
                                  wordBreak: "break-word",
                                }}
                              >
                                {finalCriminalDoc.key.slice(0, 6)}
                              </p>
                            </div>
                            <a
                              href={finalCriminalDoc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                fontSize: "10px",
                                color: "#7551FF",
                                marginTop: "4px",
                                textDecoration: "underline",
                                cursor: "pointer",
                                display: "block",
                                textAlign: "center",
                              }}
                            >
                              {t("Download")}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Botões reorganizados */}
                  <div className="button-save-cancel mt-3 flex justify-end">
                    <Button
                      className=" cancel-button my-2 ms-1 sm:my-0"
                      onClick={() => navigate("/admin/drivers")}
                    >
                      {t("back")}
                    </Button>

                    <div className="flex items-center">
                      {approved === Types.ApprovalStatus.PENDING && (
                        <>
                          <Button
                            className="approve-modal-button mx-2"
                            onClick={() => {
                              handleApprove(params.id);
                            }}
                          >
                            {t("approve")}
                          </Button>
                          <Button
                            className="delete-modal-button mx-2"
                            onClick={() => {
                              handleClickForRejectDoc(params.id);
                            }}
                          >
                            {t("reject")}
                          </Button>
                        </>
                      )}
                      {approved === Types.ApprovalStatus.REJECTED && (
                        <Button
                          className="approve-modal-button mx-2"
                          onClick={() => {
                            handleApprove(params.id);
                          }}
                        >
                          {t("approve")}
                        </Button>
                      )}
                      {approved === Types.ApprovalStatus.APPROVED && (
                        <Button
                          className="delete-modal-button mx-2"
                          onClick={() => {
                            handleClickForRejectDoc(params.id);
                          }}
                        >
                          {t("reject")}
                        </Button>
                      )}
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </Card>
      )}
    </>
  );
};

export default DriverForm;
